import React, { useEffect, useState } from "react";
import moment from "moment";
import jwtDecode from "jwt-decode";
import CustomDatePickerMui from "../../models/inputs/DateInput";
import { dateFormated } from "../../functionalComponents/dataFormatFunctions";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import ClearIcon from "@material-ui/icons/Clear";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  Button,
  Link,
  CircularProgress,
} from "@material-ui/core";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import axios from "axios";
import { appUrl } from "../../../../../../utils/config";
import { useDispatch } from "react-redux";
import {
  DESTROY_BACKDROP,
  OPEN_BACKDROP,
} from "../../../../containers/actions/types";
import InfoTooltip from "../../models/InfoTooltip";
import ViewStructuredInformation from "../../sasl/batch/ViewStructuredInformation";

// BCM-972: the requestType /requestPlannedMFGStartDateAccessDirect and
// /fetchPlannedMFGStartDateAccessRequestStatusDirect key their response
// under — confirmed against the actual endpoints, not the BCM-891 batch
// allocation ones (which use their own dedicated 'EDIT_ACCESS' request type
// on a different pair of endpoints).
const PLANNED_MFG_START_DATE_REQUEST_TYPE = "PLANNED_MFG_EDIT_ACCESS";

// BCM-972 (doc slide 1, "Display and Editing of the Planned MFG Start Date Field"):
// pencil-icon + date-picker editor for the new Planned MFG Start Date field,
// editable only by CMO users with the role of Admin, Customer Service Planner
// or Account Manager (resolved server-side into featuresAccess.editPlannedMfgStartDate,
// matching how every other role-gated field in this folder is authorized).
const EditPlannedMfgStartDate = ({ row, handleRefresh, featuresAccess, onAccessRequestResult }) => {
  const sl = row?.scheduleLineDetails || {};
  // BCM-972: fetchSLFromPODashboard returns this as scheduleLineDetails.plannedMFGStartDate.
  const [plannedMfgStartDate, setPlannedMfgStartDate] = useState(
    sl.plannedMFGStartDate || null
  );
  const [edit, setEdit] = useState(false);
  const dispatch = useDispatch();
  const tokenData = jwtDecode(localStorage.getItem("authToken"));

  useEffect(() => {
    setPlannedMfgStartDate(sl.plannedMFGStartDate || null);
  }, [sl.plannedMFGStartDate]);

  // BCM-972 (doc, "Highlighting of the Planned MFG Start Field"): red when the
  // (earliest, if multiple) Expiry Date of the Allocated Batch is before the
  // Planned MFG Start Date; standard colour otherwise, including when no
  // batch is allocated.
  const isPastAllocatedBatchExpiry =
    sl.allocatedBatchExpiryDate &&
    plannedMfgStartDate &&
    new Date(sl.allocatedBatchExpiryDate) < new Date(plannedMfgStartDate);

  const handleSave = () => {
    setEdit(false);
    dispatch({ type: OPEN_BACKDROP });
    // BCM-972: updateSLForPOMgmt expects this full set of SL-managed fields
    // together, not just the one being changed — every field here besides
    // plannedMFGStartDate is an unmodified pass-through of the value
    // fetchSLFromPODashboard already returned on scheduleLineDetails.
    const requestObj = {
      schrockenSLID: sl.schrockenSLID ?? null,
      vendorMosymphonyID: sl.vendorMosymphonyID ?? null,
      
      plannedMFGStartDate: plannedMfgStartDate || null,
     
    };
    axios
      .post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateSLForPOMgmt`,
        requestObj
      )
      .then((res) => {
        dispatch({ type: DESTROY_BACKDROP });
        if (res.data.status) {
          if (typeof handleRefresh === "function") handleRefresh();
        }
      })
      .catch((err) => {
        dispatch({ type: DESTROY_BACKDROP });
        console.error("Error saving Planned MFG Start Date:", err);
      });
  };

  const handleCancel = () => {
    setPlannedMfgStartDate(sl.plannedMFGStartDate || null);
    setEdit(false);
  };

  const dateTextStyle = { color: isPastAllocatedBatchExpiry ? "red" : "inherit" };

  // BCM-972 ("Post-consumption view" slide): once the batch's post-consumption
  // documents are ingested, editing is locked — grayed-out pencil + a tooltip
  // explaining why, instead of the active orange pencil. Locked whenever
  // fetchSLFromPODashboard's batchDetails.bomConsumptionAddedIndicator is true
  // (merged fresh onto row.batchDetails in POTabs/index.jsx's timeQtyDetailsData).
  const isLocked = row?.batchDetails?.bomConsumptionAddedIndicator === true;

  // BCM-972: PO Number / Batch Number for the tooltip's meta line, plus the raw
  // batchDetails ViewStructuredInformation ("View batch documents" → "View
  // Digitalized Information") needs — from fetchDeliveryScheduleBatchDetails,
  // no second fetch.
  const batchDetails = sl.deliveryScheduleBatchDetails;
  const poNumber = sl?.scheduleAgreementNumber;
  const batchNumber = batchDetails?.vendorBatchNumber;
  // BCM-972: /requestPlannedMFGStartDateAccessDirect's productCode/productDescription
  // — fetchPurchaseOrdersNewDirect returns these on scheduleLineDetails as
  // fgMaterialCode/fgMaterialName.
  const productCode = sl.fgMaterialCode;
  const productDescription = sl.fgMaterialName;
  // BCM-972: scoped to Post-Manufacturing docs only, same filter as the
  // checklist above — "Ingested" means when the last of those arrived, not
  // the last file of any category.
  const latestIngestedDate = (batchDetails?.customFilesList || [])
    .filter((file) => file?.documentCategoryType === "Post-Manufacturing")
    .map((file) => file?.lastUpdatedDate)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0];
  const ingestedDisplay = latestIngestedDate
    ? moment(latestIngestedDate).format("DD-MMM-YYYY, HH:mm").toUpperCase()
    : "";

  const [showDigitizedInfo, setShowDigitizedInfo] = useState(false);

  // BCM-972 ("Tooltip for edit button grayed out - missing feature access"
  // slide): same request-access flow BCM-891 wired up for Planned Allocated
  // Qty. (LotAllocationTable.jsx) — an in-app notification to the BMS admin
  // through /requestBatchAllocationAccessDirect, with the outstanding request
  // (and its 48h cooldown) read back via /fetchBatchAllocationAccessRequestStatusDirect
  // so the same request is not fired repeatedly. The confirmation banner itself
  // is owned by PONegotiationTable (onAccessRequestResult) so it shows below the
  // Time and Qty. Details table, matching where BCM-891 shows it under its table.
  const [accessRequestBusy, setAccessRequestBusy] = useState(false);
  const [priorAccessRequest, setPriorAccessRequest] = useState(null);

  // BCM-972: one concise banner — no cooldown/"already sent" explanation, no
  // notified-admins list — matching the reference note exactly. Shown for a
  // request found on load (fetch-status) the same as one just raised (POST),
  // since the reference draws no distinction between the two.
  const buildAccessRequestBanner = (entry) => {
    if (!entry) return null;
    const roleName = decodeURIComponent(localStorage.getItem("roleName") || "");
    // requestedAtDisplay is the API's own UTC-formatted string — shown as-is,
    // labelled UTC, rather than converted to the viewer's local time.
    const when = entry.requestedAtDisplay ? `${entry.requestedAtDisplay} UTC` : "";
    return {
      severity: "success",
      boldLead: "Request sent",
      rest:
        ` for edit access to Planned MFG Start Date (role: ${roleName}, PO ${poNumber || "—"}), ` +
        `to Admin on ${when}.`,
      secondary: `Ref ${entry.referenceNumber} · you'll get an email when it's approved.`,
    };
  };

  const hasPlannedMfgStartDateAccess = featuresAccess?.plannedMFGStartDate === true;
  const lockSchrockenSLID = sl.schrockenSLID;

  useEffect(() => {
    if (hasPlannedMfgStartDateAccess || !lockSchrockenSLID) return undefined;
    let cancelled = false;
    axios
      .post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchPlannedMFGStartDateAccessRequestStatusDirect`,
        { schrockenSLID: lockSchrockenSLID }
      )
      .then((res) => {
        if (cancelled || !res?.data?.status) return;
        const entry = res?.data?.data?.requests?.[PLANNED_MFG_START_DATE_REQUEST_TYPE] || null;
        setPriorAccessRequest(entry);
        // BCM-972: an empty `requests` object means no request has ever been
        // raised for this SL — the banner must not show in that case.
        if (entry) onAccessRequestResult?.(buildAccessRequestBanner(entry));
      })
      .catch(() => {
        // Non-fatal: the action is offered as before, and the middleware still
        // declines to raise a duplicate inside the window, so the worst case is
        // the user finding out on click instead of on hover.
      });
    return () => {
      cancelled = true;
    };
  }, [hasPlannedMfgStartDateAccess, lockSchrockenSLID]);

  const accessRequestCooldownActive =
    !!priorAccessRequest?.nextRequestAllowedAt &&
    new Date(priorAccessRequest.nextRequestAllowedAt).getTime() > Date.now();

  const handleRequestEditAccess = async () => {
    if (accessRequestBusy || accessRequestCooldownActive) return;
    setAccessRequestBusy(true);
    dispatch({ type: OPEN_BACKDROP });
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/requestPlannedMFGStartDateAccessDirect`;
      const res = await axios.post(url, {
        schrockenSLID: lockSchrockenSLID,
        poNumber,
        productCode,
        productDescription,
        brand: sl.brand,
        vendorMosymphonyID: sl.vendorMosymphonyID,
        brandOwnerMosymphonyID: sl.brandOwnerMosymphonyID,
        requestedBy: tokenData?.email_address,
        requestedByName: tokenData?.full_name || tokenData?.email_address,
        requestedBySaasUserID: tokenData?.saas_user_id,
        roleName: decodeURIComponent(localStorage.getItem("roleName") || ""),
        // Spec: "current value shown in tooltip e.g. 15-APR-2025" — same
        // DD-MMM-YYYY format dateFormated already renders it in, not ISO.
        plannedMFGStartDate: dateFormated(plannedMfgStartDate, "DD-MMM-YYYY", null),
      });
      if (res?.data?.status) {
        const d = res.data.data || {};
        setPriorAccessRequest(d);
        onAccessRequestResult?.(buildAccessRequestBanner(d));
      } else {
        onAccessRequestResult?.({
          severity: "error",
          boldLead: "",
          rest: res?.data?.errorDescription || res?.data?.errorMessage || "The request could not be raised.",
          secondary: "",
        });
      }
    } catch (err) {
      onAccessRequestResult?.({
        severity: "error",
        boldLead: "",
        rest: "The request could not be raised. Please try again.",
        secondary: "",
      });
    } finally {
      setAccessRequestBusy(false);
      dispatch({ type: DESTROY_BACKDROP });
    }
  };


  return (
    <div>
      {localStorage.collabType === "CMO" && featuresAccess?.plannedMFGStartDate && (
        edit ? (
          <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <CustomDatePickerMui
              value={plannedMfgStartDate}
              onChange={(date) =>
                setPlannedMfgStartDate(
                  new Date(date).toLocaleDateString("en-CA").split("T")[0] + "T00:00"
                )
              }
              style={{ width: "150px" }}
            />
            <Box style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
              <span title="save">
                <SaveIcon
                  onClick={handleSave}
                  style={{ cursor: "pointer", fontSize: "small" }}
                />
              </span>
              <span title="clear">
                <ClearIcon
                  onClick={handleCancel}
                  style={{ cursor: "pointer", fontSize: "small" }}
                />
              </span>
            </Box>
          </Box>
        ) : (
          <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={dateTextStyle}>
              {dateFormated(plannedMfgStartDate, "DD-MMM-YYYY", "")}
            </span>
            {isLocked ? (
              // BCM-972: don't mount the Tooltip at all while the full-screen
              // dialog it opens is up — MUI's Tooltip is hover-driven, and the
              // dialog opening on click (rather than a real mouseleave) left it
              // stuck open, floating over the dialog underneath it.
              showDigitizedInfo ? (
                <EditIcon
                  fontSize="large"
                  style={{ cursor: "default", color: "#bdbdbd" }}
                />
              ) : (
                <InfoTooltip
                  icon={
                    <EditIcon
                      fontSize="large"
                      style={{ cursor: "default", color: "#bdbdbd" }}
                    />
                  }
                >
                  <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#fff" }}>
                    Locked — batch documents received
                  </Typography>
                  <Typography variant="body2" style={{ color: "#e0e0e0", margin: "6px 0 10px" }}>
                    Editing of Planned MFG Start Date field is disabled once they are
                    ingested successfully.
                  </Typography>
                  <Box style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                    {/* BCM-972: customFileType names of the files whose documentCategoryType
                        is "Post-Manufacturing" (fetchDeliveryScheduleBatchDetails, POTabs/index.jsx),
                        e.g. "Certificate of Manufacture", "Certificate of Analysis". */}
                    {(sl.postManufacturingDocumentTypes || []).map((docType) => (
                      <Typography key={docType} variant="body2" style={{ color: "#e0e0e0" }}>
                        <span style={{ color: "#81c784" }}>✓</span> {docType}
                      </Typography>
                    ))}
                  </Box>
                  <Typography variant="caption" style={{ color: "#9e9e9e", display: "block", marginBottom: 6 }}>
                    PO {poNumber || "—"} · Batch {batchNumber || "—"} · Ingested{" "}
                    {ingestedDisplay || "—"}
                  </Typography>
                  <Typography
                    component="span"
                    onClick={() => setShowDigitizedInfo(true)}
                    style={{ color: "#64b5f6", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}
                  >
                    View batch documents
                  </Typography>
                </InfoTooltip>
              )
            ) : (
              <span title="Edit">
                <EditIcon
                  fontSize="large"
                  disabled={!featuresAccess?.plannedMFGStartDate}
                  onClick={() =>
                    setEdit(true)
                  }
                  style={{
                    cursor: "pointer",
                    color: featuresAccess?.plannedMFGStartDate
                      ? "#ec6535"
                      : "#bdbdbd",
                  }}
                />
              </span>
            )}
          </Box>
        )
      )}

      {/* BCM-972 ("Tooltip for edit button grayed out - missing feature access"
          slide): CMO user would otherwise see the pencil (collabType is true) but lacks featuresAccess?.plannedMFGStartDate — grayed pencil +
          a tooltip explaining why, with a "Request edit access" stub action. */}
      {localStorage.collabType === "CMO" && !isLocked &&
        !featuresAccess?.plannedMFGStartDate && (
        <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={dateTextStyle}>
            {dateFormated(plannedMfgStartDate, "DD-MMM-YYYY", "")}
          </span>
          <InfoTooltip
            icon={
              <EditIcon
                disabled={!featuresAccess?.plannedMFGStartDate}
                fontSize="large"
                style={{ cursor: "default", color: "#bdbdbd" }}
              />
            }
          >
            <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#fff" }}>
              You Don't Have Access to Edit Planned MFG Start Date
            </Typography>
            <Typography variant="body2" style={{ color: "#e0e0e0", margin: "6px 0 10px" }}>
              You don't have access to edit Planned MFG Start Date on PO{" "}
              {poNumber || "—"}. You can view and download its current value (
              {dateFormated(plannedMfgStartDate, "DD-MMM-YYYY", "not set")}), but
              changing it needs edit access.
            </Typography>
            {priorAccessRequest && (
              <Typography variant="body2" style={{ color: "#a5d6a7", fontSize: 13, margin: "6px 0 10px" }}>
                <strong>Request sent</strong>
                {buildAccessRequestBanner(priorAccessRequest)?.rest}
              </Typography>
            )}
            {!accessRequestCooldownActive && (
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {accessRequestBusy && (
                  <CircularProgress size={13} thickness={5} style={{ color: "#64b5f6" }} />
                )}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleRequestEditAccess}
                  disabled={accessRequestBusy}
                  style={{
                    color: "#64b5f6",
                    fontSize: 13,
                    textDecoration: "underline",
                    opacity: accessRequestBusy ? 0.7 : 1,
                  }}
                >
                  {accessRequestBusy ? "Sending…" : "Request edit access"}
                </Link>
              </Box>
            )}
          </InfoTooltip>
        </Box>
      )}

      {localStorage.collabType !== "CMO" && (
        <span style={dateTextStyle}>
          {dateFormated(plannedMfgStartDate, "DD-MMM-YYYY", "")}
        </span>
      )}

      {/* BCM-972: "View batch documents" opens the same "View Digitalized
          Information" full-screen view as the Batch Status dropdown
          (BatchColumnStatusRoute.jsx), reusing the batchDetails already
          fetched above instead of fetching it again. */}
      {showDigitizedInfo && (
        <Dialog fullScreen open={showDigitizedInfo} onClose={() => setShowDigitizedInfo(false)}>
          <Box style={{ display: "flex", alignItems: "center", padding: "0.5rem 1rem" }}>
            <Button
              onClick={() => setShowDigitizedInfo(false)}
              style={{ color: "black", fontWeight: 550, textTransform: "none" }}
            >
              <KeyboardArrowLeftIcon fontSize="19" color="black" />
              Back
            </Button>
            <Typography variant="h6" style={{ fontWeight: "bold", marginLeft: "2rem" }}>
              View Digitalized Information
            </Typography>
          </Box>
          <DialogContent>
            <ViewStructuredInformation
              batchDetails={batchDetails}
              targetQuantity={sl.targetQuantity}
              schrockenDeliveryScheduleID={batchDetails?.schrockenDeliveryScheduleID}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EditPlannedMfgStartDate;
